import { Box, Button, createStyles, IconButton, makeStyles, Theme } from '@material-ui/core';
import { Close as CloseIcon, Search as SearchIcon } from '@material-ui/icons';
import clsx from 'clsx';
import { HELP_SEARCH_ROUTE } from 'features/help/constants';
import { ContentfulErrorContext } from 'features/help/hooks/ContentfulErrorContext';
import React, { ReactElement, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSearchParams } from '../../../hooks/useSearchParams';
import { SearchBar } from './SearchBar';

type SearchBarRowProps = {
  buttonSize?: 'medium' | 'large' | 'small' | undefined;
  searchBarClass?: string;
  isMobile?: boolean;
};
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: '100%',
      display: 'flex',
    },
    searchBar: {
      alignItems: 'flex-start',
      background: theme.palette.background.paper,
      borderRadius: theme.spacing(0.375),
      display: 'flex',
      flexDirection: 'column',
      height: 42,
      justifyContent: 'center',
      maxWidth: 475,
      minWidth: theme.spacing(40),
      '& [class*="MuiOutlinedInput-adornedStart"]': {
        paddingLeft: 12,
      },
    },
    mobileSearchBar: {
      minWidth: 'auto',
      maxWidth: 'none',
      flexGrow: 1,
    },
    input: {
      ...theme.typography.body2,
      '&::placeholder': {
        color: theme.palette.text.disabled,
        fontFeatureSettings: "'clig' off, 'liga' off;",
        opacity: 1,
      },
      '&.MuiInputBase-inputMarginDense': {
        padding: 0,
      },
      '&.MuiInputBase-input': {
        padding: 0,
      },
      '& + fieldset': {
        border: 0,
      },
    },
    searchButton: {
      whiteSpace: 'nowrap',
      marginLeft: theme.spacing(2),
    },
    form: {
      alignItems: 'center',
      display: 'flex',
      flexGrow: 1,
      justifyContent: 'center',
      marginBottom: 0,
      flexDirection: 'column',
      '& $searchButton': {
        marginTop: theme.spacing(2),
      },
      [theme.breakpoints.up('sm')]: {
        flexDirection: 'row',
        '& $searchButton': {
          marginTop: 0,
        },
      },
    },
    mobileSearchIconButton: {
      color: theme.palette.common.white,
    },
    mobileSearchOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1100,
      backgroundColor: theme.palette.brand.legacy,
      padding: theme.spacing(2),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    mobileCloseButton: {
      color: theme.palette.common.white,
      flexShrink: 0,
    },
    mobileForm: {
      display: 'flex',
      alignItems: 'center',
      flexGrow: 1,
      marginBottom: 0,
    },
  }),
);
function SearchBarRow({
  buttonSize = 'large',
  searchBarClass,
  isMobile = false,
}: SearchBarRowProps): ReactElement | null {
  const history = useHistory();
  const query = useSearchParams<{ q?: string }>();

  const classes = useStyles();
  const [searchText, setSearchText] = React.useState(query.q || '');
  const [searchExpanded, setSearchExpanded] = useState(false);
  const { setContentfulErrorType } = React.useContext(ContentfulErrorContext);

  useEffect(() => {
    setSearchText(query.q || '');
  }, [query.q]);

  const handleSearch = React.useCallback(
    (e: React.SyntheticEvent) => {
      setContentfulErrorType(null);
      const path = `${HELP_SEARCH_ROUTE}?q=${encodeURIComponent(searchText)}`;
      history.push(path);
      setSearchExpanded(false);
      e.preventDefault();
    },
    [history, searchText],
  );

  const handleSearchIconClick = () => {
    setSearchExpanded(true);
  };

  const handleCloseSearch = () => {
    setSearchExpanded(false);
  };

  if (isMobile) {
    return (
      <>
        {!searchExpanded && (
          <IconButton
            className={classes.mobileSearchIconButton}
            onClick={handleSearchIconClick}
            aria-label="Open search"
            data-testid="mobile-search-icon-button"
          >
            <SearchIcon />
          </IconButton>
        )}
        {searchExpanded && (
          <Box className={classes.mobileSearchOverlay} data-testid="mobile-search-overlay">
            <IconButton
              className={classes.mobileCloseButton}
              onClick={handleCloseSearch}
              aria-label="Close search"
              data-testid="mobile-search-close-button"
            >
              <CloseIcon />
            </IconButton>
            <form className={classes.mobileForm} onSubmit={handleSearch}>
              <SearchBar
                searchString={searchText}
                onSearchChange={setSearchText}
                textFieldClass={clsx(classes.searchBar, classes.mobileSearchBar, searchBarClass)}
                inputClass={classes.input}
                placeholder="Enter Search Term"
                fullWidth
              />
              <Button
                data-testid="knowledge-base-searchBar-button"
                type="submit"
                variant="contained"
                size="medium"
                color="primary"
                className={classes.searchButton}
              >
                Search
              </Button>
            </form>
          </Box>
        )}
      </>
    );
  }

  return (
    <div data-testid="knowledge-base-searchBar-row" className={classes.container}>
      <form className={classes.form} onSubmit={handleSearch}>
        <SearchBar
          searchString={searchText}
          onSearchChange={setSearchText}
          textFieldClass={clsx(classes.searchBar, searchBarClass)}
          inputClass={classes.input}
          placeholder="Enter Search Term"
          fullWidth
        />
        <Button
          data-testid="knowledge-base-searchBar-button"
          type="submit"
          variant="contained"
          size={buttonSize}
          color="primary"
          className={classes.searchButton}
        >
          Search Articles
        </Button>
      </form>
    </div>
  );
}

export { SearchBarRow };
